import { BackLink, Image, Intro, Text } from "components/commons";
import { FormProvider } from "contexts/form-context";
import { FormMode, Path } from "enums";
import locale from "localization";
import { useCallback, useContext, useMemo } from "react";
import initialState from "./delivery-log-form.state";
import DeliveryLogForm from "./delivery-log-form/delilvery-log-form";
import { useApi, useModal, useMountAsync, useRouter } from "hooks";
import ViewDeliveryLog from "./view-delivery-log";
import { createDeliveryLogApi, getDeliveryLogApi, updateDeliveryLogApi } from "apis";
import { ConfirmModal } from "components/modals";
import { ManDepositSuccess } from "images";
import styles from "./delivery-log-form/delivery-log.module.scss";
import { StationContext, UserContext } from "contexts";
import { combineDateAndTimeInput } from "utils";
import useProducts from "hooks/useProducts";
import { stringToDecimal } from "utils/text.utils";
const DeliverLogDetailsModule = () => {
  const router = useRouter();
  const { user } = useContext(UserContext);
  const { station } = useContext(StationContext);
  const formMode = router.query?.mode || router.query?.pageMode || FormMode.Add;
  const isAdd = formMode === FormMode.Add;
  const isView = formMode === FormMode.View;
  const { setAllProducts } = useProducts({
    withStationCode: true,
  });

  const deliveryId = router?.query?.id;
  const confirmModal = useModal();
  const successModal = useModal();
  const { request: createDeliveryLog, loading: isCreating } = useApi({
    api: createDeliveryLogApi,
  });
  const { request: updateDeliveryLog, loading: isUpdating } = useApi({
    api: updateDeliveryLogApi,
    modalError: true,
  });
  const {
    request: getDeliveryLog,
    result: deliveryLog,
    loading: isGetting,
  } = useApi({
    api: getDeliveryLogApi,
  });
  const handleOnSubmit = useCallback(
    async (fields, error) => {
      const {
        recipient,
        shiftId,
        date,
        time,
        poReferenceNumber,
        plateNumber,
        witness,
        productType,
        products,
      } = fields;

      const payload = {
        stationCode: station.stationCode,
        recipient: recipient.value?.toLowerCase(),
        shiftId: shiftId.value,
        deliveredAt: combineDateAndTimeInput(date.value, time.value),
        poReferenceNumber: poReferenceNumber.value,
        plateNumber: plateNumber.value,
        witnesses: witness.value.map(({ witness, position }) => ({
          name: witness,
          position,
        })),
        productType: productType.value,
        products: products.value.map(({ sku, ...rest }) => {
          let value = {};
          if (productType.value === "fuel") {
            value = {
              quantityBeforeReceivingCm: stringToDecimal(rest?.quantityBeforeReceivingMm),
              quantityBeforeReceivingL: stringToDecimal(rest?.quantityBeforeReceivingLiter),
              quantityDeliveredL: stringToDecimal(rest?.quantityDelivered),
              quantityAfterReceivingCm: stringToDecimal(rest?.quantityAfterReceivingMm),
              quantityAfterReceivingL: stringToDecimal(rest?.quantityAfterReceivingLiter),
            };
          } else {
            value = {
              quantityBeforeReceiving: stringToDecimal(rest?.quantityBeforeReceiving),
              quantityDelivered: stringToDecimal(rest?.quantityDelivered),
              quantityAfterReceiving: stringToDecimal(rest?.quantityAfterReceiving),
            };
          }

          return {
            product: sku,
            ...value,
          };
        }),
        userId: user?.userId,
      };

      const proceed = async () => {
        const res = isAdd
          ? await createDeliveryLog(payload)
          : await updateDeliveryLog(
              {
                id: deliveryId,
                ...payload,
              },
              () => handleOnSubmit(fields, error),
              { reqError: true }
            );

        if (res?.error) {
          return;
        }
        confirmModal.close();
        successModal.show({
          primary: {
            text: locale.gotIt,
            onClick: () => {
              router.push(Path.DeliveryLogs);
            },
          },
        });
      };

      confirmModal.show({
        primary: {
          text: locale.proceed,
          onClick: () => {
            proceed();
          },
        },
        secondary: {
          text: locale.close,
        },
      });
    },
    [
      confirmModal,
      createDeliveryLog,
      deliveryId,
      isAdd,
      router,
      station.stationCode,
      successModal,
      updateDeliveryLog,
      user?.userId,
    ]
  );

  useMountAsync(
    async () => {
      if (!isAdd) {
        await getDeliveryLog({
          deliveryId: deliveryId,
        });
      }
    },
    () => {
      setAllProducts([]);
    }
  );

  return (
    <>
      <BackLink text={locale.deliveryLogs} path={Path.DeliveryLogs} />
      <Intro
        title={
          isAdd
            ? locale.addDeliverLog
            : `${locale.poReferenceNumber} ${deliveryLog?.poReferenceNumber || "--"}`
        }
      />
      <br />
      <FormProvider
        initialState={useMemo(() => initialState({ deliveryLog }), [deliveryLog])}
        onSubmit={handleOnSubmit}
      >
        {isView ? (
          <ViewDeliveryLog deliveryLog={deliveryLog} loading={isGetting} />
        ) : (
          <DeliveryLogForm
            loading={isCreating || isUpdating || isGetting}
            deliveryLog={deliveryLog}
          />
        )}
      </FormProvider>
      <ConfirmModal
        {...confirmModal}
        primary={{
          ...confirmModal?.primary,
          loading: isCreating || isUpdating,
          disabled: isCreating || isUpdating,
        }}
        closable={false}
        centerActions
      >
        <div className={styles.successModal}>
          <Image src={ManDepositSuccess} />
          <br />
          <Text strong className={styles.title}>
            {isAdd ? locale.addDeliverLog : locale.saveDeliveryLog}?
          </Text>
          <Text className={styles.message}>{locale.confirmSaveDeliveryLog}</Text>
        </div>
      </ConfirmModal>
      <ConfirmModal {...successModal} closable={false} centerActions>
        <div className={styles.successModal}>
          <Image src={ManDepositSuccess} />
          <br />
          <Text strong className={styles.title}>
            {locale.deliveryLogRecorded}
          </Text>
          <Text className={styles.message}>{locale.deliveryLogRecordedMessage}</Text>
        </div>
      </ConfirmModal>
    </>
  );
};

export default DeliverLogDetailsModule;
